import type { Action, History, Location } from 'history';
import { createContext, lazy } from 'react';
import type { OverrideProperties } from 'type-fest';

import historyInstance from '@/history';
import routerInstance from '@/router';
import type {
  LooseRouteParams,
  ResolvedRoute,
  RouteMetadata,
} from '@routes/types';

/**
 * This is a static history stub used on the server side to prevent errors
 * when accessing the `history` context. It provides a no-op implementation
 * of the History API methods, which is helpful in SSR.
 */
const staticHistory = new Proxy<History>({} as History, {
  get(target, property, receiver) {
    if (__DEV__ && !(property in target)) {
      // TODO: Consider throwing an error in dev to catch possible issues
      console.warn(`history.${String(property)} is a no-op on the server`);
    }

    return Reflect.get(target, property, receiver);
  },
});

export const NavigatorContext = createContext(historyInstance ?? staticHistory);

export interface LocationContextType {
  location: Location;
  action: Action;
  isPending: boolean;
}

export const LocationContext = createContext<LocationContextType>(null!);

export const RouterContext = createContext(routerInstance);

export type RouteContextType<
  Params extends LooseRouteParams = LooseRouteParams,
> = OverrideProperties<
  ResolvedRoute<Params>,
  {
    route: ResolvedRoute<Params>['route'] & RouteMetadata;
  }
>;

export const RouteContext = createContext<RouteContextType>({
  baseUrl: '',
  chunks: [],
  description: '',
  params: {},
  pathname: '',
  query: {},
  route: { path: '' },
  routeMask: '',
  title: '',
  Component: lazy(() => Promise.resolve({ default: () => null })),
});

export const RouteProvider = RouteContext.Provider;
